import { type RumInitConfiguration, datadogRum } from "@datadog/browser-rum";
import { getAppInfo } from "@src/appV2/NativeAdapters/app.adapter";
import { getCurrentBundleId } from "@src/appV2/NativeAdapters/liveUpdate.adapter";
import {
  DeploymentEnvironmentName,
  environmentConfig,
  isProductionNodeEnvironment,
} from "@src/appV2/environment";
import { ROLE_MAPPINGS } from "@src/appV2/FacilityUser/roles";
import { Platform } from "@src/mobile/utils/platform";
import { SessionType } from "src/modules/interface";

interface RumEventForFiltering {
  type: string;
  error?: {
    message?: string;
    stack?: string;
    type?: string;
    resource?: {
      url?: string;
    };
  };
}

const SEGMENT_ANALYTICS_CDN_HOST_PATTERN = /(^|[^\w.-])cdn\.segment\.com(?=[:/?#]|$)/i;
const SEGMENT_ANALYTICS_DESTINATION_BUNDLE_PATTERN = /ajs-destination\.bundle/i;
const CHUNK_LOAD_ERROR_PATTERN = /ChunkLoadError/i;

// WebKit/Safari (including iOS WKWebView) emits this UnknownError from its own
// IndexedDB engine when a delete-range / cursor delete runs after the enclosing
// transaction has auto-deactivated — e.g. async work between cursor steps or a
// page-lifecycle freeze / back-forward-cache transition. In admin-web-app the only IndexedDB
// writer is the Braze Web SDK (`shared-lib/indexed-db-adapter.js`, which does
// `openCursor` + `delete` inside readwrite transactions); repo code never touches
// IndexedDB directly. WebKit throws asynchronously past the SDK callback, so RUM
// captures it as an unhandled error with no JS stack frames, leaving nothing
// actionable on our side. Datadog samples show 99.6% of occurrences are WebKit,
// and the message wording is WebKit-specific, so matching on it does not hide
// IndexedDB failures surfaced by other engines (QuotaExceededError,
// ConstraintError, version-change aborts, etc. all use different messages).
// See STAFF-1355.
const WEBKIT_INDEXED_DB_DELETE_RANGE_ERROR_MESSAGE =
  "Attempt to delete range from database without an in-progress transaction";

// Segment's `analytics.js` (loaded at runtime from `cdn.segment.com` by the
// snippet in `src/components/segmentSnippet.ts`) calls `console.error` with
// these vendor-specific prefixes when one of its own network requests fails:
// the metrics/telemetry beacon and the destination/integration script load,
// respectively. RUM's console-error forwarding then reports each one to Error
// Tracking, where they group under a single generic `TypeError: Load failed`
// fingerprint (the stack is just the error type). Datadog samples over 30 days
// (~1,810 of ~1,877 events) show this is ~98% iOS WebKit — Mobile Safari and
// in-app WKWebViews, where Segment fetches routinely fail on flaky mobile
// networks, page-lifecycle freezes, and tracking blockers. The failing fetch is
// entirely inside the third-party CDN bundle, so there is no first-party call
// site to fix; the errors are non-actionable vendor noise. Match only the
// Segment-specific message markers on `type === "error"` events so first-party
// `Load failed` network errors stay visible. Same vendor family as the Segment
// chunk-load suppression (STAFF-1334). See STAFF-1347.
const SEGMENT_ANALYTICS_NETWORK_CONSOLE_ERROR_MARKERS = [
  "Error sending segment performance metrics",
  "[analytics.js] Failed to load Analytics.js",
];

// Sendbird's Chat SDK throws `SendbirdError: There was a network error.`
// (`SendbirdErrorCode.NETWORK_ERROR`, 800120) whenever one of its websocket /
// API requests fails on a dropped or flaky connection. The SDK is connected
// app-wide (root `ChatProvider` in `src/index.tsx`) and runs channel-sync /
// reconnection requests in the background on every page, so on transient
// network loss it rejects from inside the SDK bundle and RUM captures it as an
// unhandled error with no actionable first-party stack frame. Datadog samples
// (issue f6292cdc-d16a-11ee-af53-da7ad0900002) show 688 sessions / 1,054 events
// over 7 days spread across every page with no user action; ~25% of events are
// fully offline and ~18% on 2g/3g, and NETWORK_ERROR is transient by
// definition. Match only this exact Sendbird error name + message on
// `type === "error"` events so unexpected Sendbird failures (auth, server, and
// other unexpected codes/messages) stay visible. See STAFF-1341.
const SENDBIRD_ERROR_TYPE = "SendbirdError";
const SENDBIRD_NETWORK_ERROR_MESSAGE = "There was a network error.";

// Sendbird's Chat SDK rejects the initial `connect()` with a SendbirdError of
// "User has been deactivated." once the corresponding Sendbird user is
// deactivated server-side. admin-web-app mounts a single SendbirdProvider at the
// app root (`ChatProvider` in src/containers/chat/chat.tsx) that connects every
// authenticated session on load, so a deactivated / offboarded account whose
// browser still holds a valid CBH session and a stale `sendBirdAccessToken`
// hits this on every page load until the session is cleared — which is why it
// spans far more sessions than just the chat page. The uikit connect-promise
// rejection is caught internally with a no-op logger, so the error only
// surfaces from the Sendbird SDK bundle itself; there is no first-party call
// site to demote it at. It is an expected account/session-transition state with
// no frontend remediation, so we drop it from Error Tracking. The match requires
// both a Sendbird origin and the deactivated-user message, so every other
// Sendbird failure (network drops, auth/token errors, unexpected codes) and any
// non-Sendbird "deactivated" error still reaches Error Tracking with full
// context. See STAFF-1345.
const SENDBIRD_DEACTIVATED_USER_ERROR_MESSAGE = "User has been deactivated";
const SENDBIRD_ERROR_ORIGIN_PATTERN = /sendbird/i;

const datadogRumInitConfig: RumInitConfiguration = {
  applicationId: environmentConfig.REACT_APP_ADMIN_DATADOG_RUM_APPLICATION_ID,
  clientToken: environmentConfig.REACT_APP_ADMIN_DATADOG_RUM_CLIENT_TOKEN,
  service: "admin-web-app",
  defaultPrivacyLevel: "allow",
  version: __APP_VERSION__ || "1.0.0",
  sessionSampleRate: 100,
  sessionReplaySampleRate: 100,
  trackUserInteractions: true,
  trackResources: true,
  trackLongTasks: true,
  env: environmentConfig.REACT_APP_ENVIRONMENT_NAME,
  allowedTracingUrls: [
    environmentConfig.REACT_APP_API_MAIN_URL,
    /^https:\/\/.*\.prod\.clipboardhealth\.org/,
    /^https:\/\/.*\.clipboard\.health/,
  ],
  // iOS Capacitor WebViews can block cookies; fall back to localStorage.
  // `allowFallbackToLocalStorage` was removed in @datadog/browser-rum v7 — the
  // localStorage fallback is NOT automatic, so it must be requested explicitly
  // via `sessionPersistence`. Without it, iOS sessions stop being recorded.
  // See: https://github.com/DataDog/browser-sdk/issues/480
  sessionPersistence: ["cookie", "local-storage"],
  enableExperimentalFeatures: ["feature_flags"],
};

/**
 * Encodes a semver string as a single comparable number, e.g. "1.1733.1" → 117_330_001
 * (major * 1e8 + minor * 1e4 + patch). The RUM `version` tag is a string, so range
 * queries like `version:>=1.1733.2` compare lexicographically and mis-sort once a
 * segment changes digit count ("1.1733.10" < "1.1733.2"). The Releases & OTA
 * dashboard's version-adoption widgets use `@context.versionNumber` numeric ranges
 * instead. See observability/README.md.
 */
export function parseVersionNumber(version: string): number | undefined {
  const match = /^(\d+)\.(\d+)\.(\d+)$/.exec(version);
  if (match === null) {
    return undefined;
  }

  const [, major, minor, patch] = match;
  if (Number(minor) >= 10_000 || Number(patch) >= 10_000) {
    return undefined;
  }

  return Number(major) * 100_000_000 + Number(minor) * 10_000 + Number(patch);
}

export function shouldSendDatadogRumEvent(event: RumEventForFiltering): boolean {
  return (
    !isSegmentAnalyticsChunkLoadError(event) &&
    !isWebKitIndexedDbDeleteRangeError(event) &&
    !isSegmentAnalyticsNetworkConsoleError(event) &&
    !isSendbirdNetworkError(event) &&
    !isSendbirdDeactivatedUserError(event)
  );
}

async function getCapacitorAppVersion(): Promise<string | undefined> {
  if (!Platform.isMobile) {
    return undefined;
  }

  try {
    const info = await getAppInfo();
    return info.version;
  } catch {
    return undefined;
  }
}

function isIPadOSDesktopMode(): boolean {
  // iPadOS Safari in desktop mode reports a Macintosh UA. Distinguish by
  // touch-point count, which a real Mac trackpad does not have.
  return (
    typeof navigator !== "undefined" &&
    navigator.platform === "MacIntel" &&
    typeof navigator.maxTouchPoints === "number" &&
    navigator.maxTouchPoints > 1
  );
}

function getOsFromUserAgent(userAgent: string): string {
  if (/iPhone|iPad|iPod/i.test(userAgent) || isIPadOSDesktopMode()) {
    return "ios";
  }
  if (/Android/i.test(userAgent)) {
    return "android";
  }
  if (/Windows/i.test(userAgent)) {
    return "windows";
  }
  if (/Mac OS X|Macintosh/i.test(userAgent)) {
    return "macos";
  }
  if (/Linux/i.test(userAgent)) {
    return "linux";
  }
  return "unknown";
}

export const initDatadogRum = async () => {
  if (
    isProductionNodeEnvironment() &&
    environmentConfig.REACT_APP_ENVIRONMENT_NAME === DeploymentEnvironmentName.PRODUCTION
  ) {
    const appVersion = await getCapacitorAppVersion();
    const liveUpdateBundleId = await getCurrentBundleId();
    const os = getOsFromUserAgent(navigator.userAgent);
    const versionNumber = parseVersionNumber(datadogRumInitConfig.version ?? "");

    datadogRum.init({
      ...datadogRumInitConfig,
      beforeSend(event) {
        if (!shouldSendDatadogRumEvent(event)) {
          return false;
        }

        if (event.context) {
          event.context.app = "admin";
          event.context.os = os;
          event.context.screenWidth = window.screen.width;
          event.context.screenHeight = window.screen.height;
          event.context.platform = Platform.platform;
          event.context.isMobileApp = Platform.isMobile;
          if (appVersion) {
            event.context.appVersion = appVersion;
          }
          if (versionNumber !== undefined) {
            event.context.versionNumber = versionNumber;
          }
          event.context.liveUpdateBundleId = liveUpdateBundleId;

          // Propagate identityProvider from RUM global context onto every
          // event so it survives into stored events. Monitors filter on
          // @context.identityProvider per-IdP error rate.
          const globalContext = datadogRum.getGlobalContext() as
            | { identityProvider?: unknown }
            | undefined;
          const identityProvider = globalContext?.identityProvider;
          if (
            typeof identityProvider === "string" &&
            event.context.identityProvider === undefined
          ) {
            event.context.identityProvider = identityProvider;
          }
        }
        return true;
      },
    });
  }
};

export const removeDatadogRUMUser = () => {
  datadogRum.clearUser();
};

export const identifyDatadogRumUser = (session: SessionType["session"]) => {
  const userProperties = getUserProperties(session);

  datadogRum.setUser({
    // As facility user names are not being searched for
    // we keep the facility's name instead of facility user's name for backward compatible
    name: session.profile?.name,
    email: session.user?.email,
    ...userProperties,
  });
};

export function setDatadogUserProperty(key: string, value: unknown) {
  datadogRum.setUserProperty(key, value);
}

function getSessionTypeProperties(session: SessionType["session"]) {
  if (session.type === "FACILITY") {
    return { facilityId: session.profile?.userId };
  }

  if (session.type === "AGENT") {
    return { agentId: session.profile?.userId };
  }

  return { adminId: session.profile?.userId };
}

function getFacilityRoleLabels(roles: string[] | undefined): string | undefined {
  if (!roles?.length) {
    return undefined;
  }
  return roles
    .map((roleId) => ROLE_MAPPINGS[roleId as keyof typeof ROLE_MAPPINGS]?.label ?? roleId)
    .join(", ");
}

function getUserProperties(session: SessionType["session"]) {
  if (session?.adminProfile) {
    return {
      type: "ADMIN",
      id: session.user?._id,
      sessionType: session.type,
      roles: session.adminProfile.roles,
      ...getSessionTypeProperties(session),
    };
  }

  const facilityRoles = session.user?.access;
  return {
    type: "FACILITY",
    id: session.user?._id,
    facilityId: session.profile?.userId,
    roles: facilityRoles,
    roleLabels: getFacilityRoleLabels(facilityRoles),
  };
}

function getErrorMessageStack(event: RumEventForFiltering): string {
  return [event.error?.message ?? "", event.error?.stack ?? ""].join("\n");
}

function getErrorTypeMessageStack(event: RumEventForFiltering): string {
  return [event.error?.type ?? "", event.error?.message ?? "", event.error?.stack ?? ""].join("\n");
}

function isSegmentAnalyticsChunkLoadError(event: RumEventForFiltering): boolean {
  if (event.type !== "error") {
    return false;
  }

  const errorDetails = [getErrorTypeMessageStack(event), event.error?.resource?.url ?? ""].join(
    "\n"
  );

  return (
    CHUNK_LOAD_ERROR_PATTERN.test(errorDetails) &&
    SEGMENT_ANALYTICS_CDN_HOST_PATTERN.test(errorDetails) &&
    SEGMENT_ANALYTICS_DESTINATION_BUNDLE_PATTERN.test(errorDetails)
  );
}

function isWebKitIndexedDbDeleteRangeError(event: RumEventForFiltering): boolean {
  if (event.type !== "error") {
    return false;
  }

  return getErrorMessageStack(event).includes(WEBKIT_INDEXED_DB_DELETE_RANGE_ERROR_MESSAGE);
}

function isSegmentAnalyticsNetworkConsoleError(event: RumEventForFiltering): boolean {
  if (event.type !== "error") {
    return false;
  }

  const errorDetails = getErrorMessageStack(event);

  return SEGMENT_ANALYTICS_NETWORK_CONSOLE_ERROR_MARKERS.some((marker) =>
    errorDetails.includes(marker)
  );
}

function isSendbirdNetworkError(event: RumEventForFiltering): boolean {
  if (event.type !== "error") {
    return false;
  }

  const errorDetails = getErrorTypeMessageStack(event);

  return (
    errorDetails.includes(SENDBIRD_ERROR_TYPE) &&
    errorDetails.includes(SENDBIRD_NETWORK_ERROR_MESSAGE)
  );
}

function isSendbirdDeactivatedUserError(event: RumEventForFiltering): boolean {
  if (event.type !== "error") {
    return false;
  }

  const errorDetails = getErrorTypeMessageStack(event);

  return (
    SENDBIRD_ERROR_ORIGIN_PATTERN.test(errorDetails) &&
    errorDetails.includes(SENDBIRD_DEACTIVATED_USER_ERROR_MESSAGE)
  );
}
